/**
 * IndexedDB-based message caching for the Fyncall Widget
 * Provides persistent storage for messages that survives page refreshes
 * and supports larger data than localStorage (which has ~5MB limit)
*/

import { getState, incrementUnreadCount } from '../core/state.js';

const DB_NAME = 'fyncall_widget';
const DB_VERSION = 1;
const MESSAGES_STORE = 'messages';

/** @type {IDBDatabase|null} */
let dbInstance = null;

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase>} Database instance
 */
export function initMessageDB() {
    return new Promise((resolve, reject) => {
        // Return existing instance if already initialized
        if (dbInstance) {
            resolve(dbInstance);
            return;
        }

        // Check for IndexedDB support
        if (!window.indexedDB) {
            console.warn('[FyncallMessageCache] IndexedDB not supported, falling back to localStorage');
            reject(new Error('IndexedDB not supported'));
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            // Create messages object store if it doesn't exist
            if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
                const store = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id', autoIncrement: true });
                // Index by session_id for efficient lookups
                store.createIndex('session_id', 'session_id', { unique: false });
                // Index by created_at for ordering
                store.createIndex('created_at', 'created_at', { unique: false });
                console.log('[FyncallMessageCache] Created messages store');
            }
        };

        request.onsuccess = (event) => {
            dbInstance = event.target.result;
            console.log('[FyncallMessageCache] IndexedDB initialized');
            resolve(dbInstance);
        };

        request.onerror = (event) => {
            console.error('[FyncallMessageCache] Failed to open IndexedDB:', event.target.error);
            reject(event.target.error);
        };
    });
}

/**
 * Get the database instance, initializing if needed
 * @returns {Promise<IDBDatabase>}
 */
async function getDB() {
    if (dbInstance) return dbInstance;
    return initMessageDB();
}

/**
 * Generate a unique cache ID for a message in a session
 * @param {string} sessionId - Session ID
 * @param {Object} message - Message object
 * @returns {string} Unique cache ID
 */
function generateCacheId(sessionId, message) {
    // Use message ID if available, otherwise create a signature-based ID
    if (message.id && !message.id.toString().startsWith('visitor_') && !message.id.toString().startsWith('bot_')) {
        return `${sessionId}_${message.id}`;
    }
    // For temporary IDs, use content signature
    const contentSig = (message.content || '').substring(0, 50);
    return `${sessionId}_${message.sender_type}_${contentSig}`;
}

/**
 * Get all messages for a session from IndexedDB
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} Array of messages
 */
export async function getMessagesFromCache(sessionId) {
    if (!sessionId) return [];

    try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(MESSAGES_STORE, 'readonly');
            const store = transaction.objectStore(MESSAGES_STORE);
            const index = store.index('session_id');
            const request = index.getAll(sessionId.toString());

            request.onsuccess = () => {
                const messages = request.result || [];
                // Sort by created_at
                messages.sort((a, b) => {
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return dateA - dateB;
                });
                console.log(`[FyncallMessageCache] Loaded ${messages.length} messages from IndexedDB cache`);
                resolve(messages);
            };

            request.onerror = () => {
                console.error('[FyncallMessageCache] Failed to get messages:', request.error);
                reject(request.error);
            };
        });
    } catch (error) {
        console.warn('[FyncallMessageCache] IndexedDB error, returning empty:', error);
        return [];
    }
}

/**
 * Save messages to IndexedDB (replaces existing messages for the session)
 * @param {string} sessionId - Session ID
 * @param {Array} messages - Array of message objects
 * @returns {Promise<void>}
 */
export async function saveMessagesToCache(sessionId, messages) {
    if (!sessionId || !messages || !Array.isArray(messages)) return;

    try {
        const db = await getDB();

        // First, clear existing messages for this session
        await clearSessionMessages(sessionId);

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
            const store = transaction.objectStore(MESSAGES_STORE);

            messages.forEach(message => {
                const cacheEntry = {
                    ...message,
                    session_id: sessionId.toString(),
                    cache_id: generateCacheId(sessionId, message),
                    cached_at: Date.now()
                };
                store.add(cacheEntry);
            });

            transaction.oncomplete = () => {
                console.log(`[FyncallMessageCache] Saved ${messages.length} messages to IndexedDB`);
                resolve();
            };

            transaction.onerror = () => {
                console.error('[FyncallMessageCache] Failed to save messages:', transaction.error);
                reject(transaction.error);
            };
        });
    } catch (error) {
        console.warn('[FyncallMessageCache] Failed to save messages:', error);
    }
}

/**
 * Append a single message to the cache
 * @param {string} sessionId - Session ID
 * @param {Object} message - Message object
 * @returns {Promise<void>}
 */
export async function appendMessageToIndexedDB(sessionId, message) {
    if (!sessionId || !message) return;

    try {
        const db = await getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
            const store = transaction.objectStore(MESSAGES_STORE);

            const cacheEntry = {
                ...message,
                session_id: sessionId.toString(),
                cache_id: generateCacheId(sessionId, message),
                cached_at: Date.now()
            };

            const request = store.add(cacheEntry);

            request.onsuccess = () => {
                console.log('[FyncallMessageCache] Appended message to IndexedDB');
                // If widget is closed, bump unread badge and notify host
                if (!getState().isOpen) {
                    incrementUnreadCount();
                    if (window.FyncallApp && typeof window.FyncallApp.setUnreadCount === 'function') {
                        window.FyncallApp.setUnreadCount(getState().unreadCount);
                    }
                }
                resolve();
            };

            request.onerror = () => {
                // Might be a duplicate, which is okay
                if (request.error?.name === 'ConstraintError') {
                    console.log('[FyncallMessageCache] Message already in cache, skipping');
                    resolve();
                } else {
                    console.error('[FyncallMessageCache] Failed to append message:', request.error);
                    reject(request.error);
                }
            };
        });
    } catch (error) {
        console.warn('[FyncallMessageCache] Failed to append message:', error);
    }
}

/**
 * Clear all messages for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export async function clearSessionMessages(sessionId) {
    if (!sessionId) return;

    try {
        const db = await getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
            const store = transaction.objectStore(MESSAGES_STORE);
            const index = store.index('session_id');
            const request = index.openCursor(IDBKeyRange.only(sessionId.toString()));

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                }
            };

            transaction.oncomplete = () => {
                console.log(`[FyncallMessageCache] Cleared messages for session ${sessionId}`);
                resolve();
            };

            transaction.onerror = () => {
                console.error('[FyncallMessageCache] Failed to clear messages:', transaction.error);
                reject(transaction.error);
            };
        });
    } catch (error) {
        console.warn('[FyncallMessageCache] Failed to clear session messages:', error);
    }
}

/**
 * Clear all messages older than a certain age
 * Used for cleanup of old sessions
 * @param {number} maxAgeMs - Maximum age in milliseconds (default: 24 hours)
 * @returns {Promise<void>}
 */
export async function clearOldMessages(maxAgeMs = 24 * 60 * 60 * 1000) {
    try {
        const db = await getDB();
        const cutoffTime = Date.now() - maxAgeMs;

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
            const store = transaction.objectStore(MESSAGES_STORE);
            const request = store.openCursor();

            let deletedCount = 0;

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const cachedAt = cursor.value.cached_at || 0;
                    if (cachedAt < cutoffTime) {
                        cursor.delete();
                        deletedCount++;
                    }
                    cursor.continue();
                }
            };

            transaction.oncomplete = () => {
                if (deletedCount > 0) {
                    console.log(`[FyncallMessageCache] Cleared ${deletedCount} old messages`);
                }
                resolve();
            };

            transaction.onerror = () => {
                console.error('[FyncallMessageCache] Failed to clear old messages:', transaction.error);
                reject(transaction.error);
            };
        });
    } catch (error) {
        console.warn('[FyncallMessageCache] Failed to clear old messages:', error);
    }
}

/**
 * Check if IndexedDB is available and functional
 * @returns {Promise<boolean>}
 */
export async function isIndexedDBAvailable() {
    try {
        await initMessageDB();
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Close the database connection
 * Useful for cleanup or when switching sessions
 */
export function closeMessageDB() {
    if (dbInstance) {
        dbInstance.close();
        dbInstance = null;
        console.log('[FyncallMessageCache] Database connection closed');
    }
}
